import { supabase, isSupabaseConfigured } from './supabaseClient';

// Simple in-memory cache to prevent duplicate requests
const priceCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Feedback submission function
export const submitFeedback = async (comment, sessionDetails) => {
  // Debug logs
  console.log('Submit Feedback - Session Details:', sessionDetails);
  
  // Format bundle details in a readable way
  const formatBundleDetails = (details) => {
    // Check if we have any valid component
    const hasAnyComponent = 
      details?.selectedScreen?.name || 
      details?.selectedTransducer?.name || 
      details?.selectedBattery?.name || 
      details?.selectedShuttle?.name || 
      details?.selectedPole?.name;
    
    if (!hasAnyComponent) {
      console.log('No components found in session details');
      return '\n\nNo bundle configured yet';
    }

    let bundleText = '\n\nbundleDetails\n\n';
    
    // Add each component if it exists
    if (details?.selectedScreen?.name) {
      bundleText += `Screen: ${details.selectedScreen.name}\n`;
    }
    
    if (details?.selectedTransducer?.name) {
      bundleText += `Transducer: ${details.selectedTransducer.name}\n`;
    }
    
    if (details?.selectedBattery?.name) {
      bundleText += `Battery: ${details.selectedBattery.name}\n`;
    }
    
    if (details?.selectedShuttle?.name) {
      bundleText += `Shuttle: ${details.selectedShuttle.name}\n`;
    }
    
    if (details?.selectedPole?.name) {
      bundleText += `Pole: ${details.selectedPole.name}\n`;
    }
    
    // Add total MSRP if available
    if (details?.totalMSRP) {
      bundleText += `Total MSRP: $${details.totalMSRP.toFixed(2)}`;
    }

    console.log('Formatted bundle text:', bundleText);
    return bundleText;
  };

  const formattedMessage = comment + formatBundleDetails(sessionDetails);
  console.log('Sending formatted message:', formattedMessage);

  const response = await fetch('https://formspree.io/f/mgvaevdy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      message: formattedMessage
    })
  });

  if (!response.ok) {
    throw new Error('Failed to submit feedback');
  }

  return response;
};

// ---------------------------------------------------------------------------
// Price fetch debug (set to false to silence; remove block when done debugging)
// ---------------------------------------------------------------------------
const DEBUG_PRICES = typeof process.env.REACT_APP_DEBUG_PRICES !== 'undefined'
  ? process.env.REACT_APP_DEBUG_PRICES === 'true'
  : true;
function debugPrices(...args) {
  if (DEBUG_PRICES) {
    console.log('[prices]', ...args);
  }
}

// ---------------------------------------------------------------------------
// Lookup mapping: frontend csvLookupId / productId -> latest_prices_view.product_name
// Only add entries where Supabase product_name DIFFERS from the frontend string.
// 1) Run in Supabase: SELECT DISTINCT product_name FROM latest_prices_view ORDER BY product_name;
// 2) For each frontend string that does NOT appear in that result, add: 'frontend': 'exact Supabase value'
// 3) Remove any line below if your Supabase uses the frontend string as-is (no mapping needed).
// Full list of frontend strings: docs/SUPABASE_PRICE_LOOKUP_STRINGS.md
// ---------------------------------------------------------------------------
const LOOKUP_TO_SUPABASE_PRODUCT_NAME = {
  // --- Add only mismatches. Right-hand side must exist in latest_prices_view. ---
  // Example: if Supabase has "Garmin LiveScope Plus LVS34" but frontend sends "LiveScope Plus LVS34":
  // 'LiveScope Plus LVS34': 'Garmin LiveScope Plus LVS34',
  // Example: if Supabase has "Amped Outdoors 30Ah LiFePO4 + 3A Charger":
  // '30Ah LiFePO4 + 3A Charger': 'Amped Outdoors 30Ah LiFePO4 + 3A Charger',
};

export function resolveProductNameForSupabase(lookupId) {
  if (!lookupId || typeof lookupId !== 'string') return lookupId;
  const trimmed = lookupId.trim();
  return LOOKUP_TO_SUPABASE_PRODUCT_NAME[trimmed] ?? trimmed;
}

/**
 * Fetch the set of product_name values that have at least one active offer in latest_prices_view.
 * Used by the Price Comparison Hub to hide cards for products with no offers (e.g. Amped-only after deactivation).
 */
export const fetchProductNamesWithOffers = async () => {
  if (!isSupabaseConfigured()) return new Set();
  try {
    const { data: rows, error } = await supabase
      .from('latest_prices_view')
      .select('product_name');
    if (error) {
      debugPrices('fetchProductNamesWithOffers error', error.message);
      return new Set();
    }
    const names = [...new Set((rows || []).map((r) => r.product_name).filter(Boolean))];
    return new Set(names);
  } catch (err) {
    debugPrices('fetchProductNamesWithOffers error', err?.message);
    return new Set();
  }
};

/**
 * Normalize price from Supabase (number or string) to UI format.
 * Returns { priceStr, numericPrice } for use in processed row.
 */
function normalizePrice(price) {
  if (price == null || price === '') {
    return { priceStr: 'N/A', numericPrice: 0 };
  }
  const num = typeof price === 'number' ? price : parseFloat(String(price).replace(/[$,]/g, '') || '0');
  const numericPrice = isNaN(num) ? 0 : num;
  const priceStr = typeof price === 'number' ? `$${price.toFixed(2)}` : (price || 'N/A');
  return { priceStr, numericPrice };
}

/**
 * Map a raw latest_prices_view row to the UI-friendly shape expected by SimplePriceComparison.
 * Used by getOffersByProductId and keeps the same contract as fetchProductPricesFromCSV output.
 */
function mapLatestPricesRowToUI(row) {
  const priceValue = row.current_price != null ? row.current_price : row.price;
  const { priceStr, numericPrice } = normalizePrice(priceValue);
  return {
    Price: priceStr,
    Product: row.product_name || 'Unknown Product',
    'Date Time Stamp': row.last_checked_at || new Date().toISOString(),
    Source: row.website || 'Unknown',
    'Job Link': row.affiliate_url || row.affiliate_link || '',
    numeric_price: numericPrice
  };
}

// =============================================================================
// DB-driven product/offers API (slug -> products -> product_id -> latest_prices_view)
// Use these for the new reusable product comparison page; do not use product_name
// as the primary path for the DB-driven flow.
// =============================================================================

// TEMPORARY: Slug alias map so legacy URLs resolve to the canonical product row in DB.
// Remove when products table has a single canonical row per product (e.g. slug cleanup/merge).
const SLUG_ALIAS_TO_CANONICAL = {
  'livescope-plus-lvs34': 'garmin-livescope-plus-lvs34',
};

/**
 * Fetch a single product by slug from the products table.
 *
 * @param {string} slug - URL slug (e.g. "livescope-plus-lvs34")
 * @returns {Promise<{ id, name, slug, image_url, msrp, ... } | null>}
 *   Product row, or null if not found or Supabase not configured.
 *   Throws on Supabase error (e.g. network). PGRST116 (no rows) returns null.
 */
export const getProductBySlug = async (slug) => {
  if (!slug || typeof slug !== 'string') return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;
  if (!isSupabaseConfigured()) {
    debugPrices('getProductBySlug: Supabase not configured');
    return null;
  }
  // Resolve alias so legacy slugs (e.g. livescope-plus-lvs34) hit the canonical row
  const querySlug = SLUG_ALIAS_TO_CANONICAL[trimmed] ?? trimmed;
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id, brand, name, slug, category, msrp, upc, mpn, image_url, is_active')
      .eq('slug', querySlug)
      .maybeSingle();
    if (error) {
      debugPrices('getProductBySlug error', error.message);
      throw new Error(error.message || 'Failed to fetch product by slug');
    }
    return data || null;
  } catch (err) {
    debugPrices('getProductBySlug error', err?.message);
    throw err;
  }
};

/**
 * Fetch all offers for a product from latest_prices_view by product_id, ordered by price ascending.
 * Returns the same UI-friendly shape as fetchProductPricesFromCSV so SimplePriceComparison can use it.
 *
 * @param {string} productId - UUID (or id) of the product from the products table
 * @returns {Promise<{ success: boolean, data: Array<{ Price, Product, 'Date Time Stamp', Source, 'Job Link', numeric_price }>, source: string }>}
 *   success: true and data array (possibly empty); source: 'supabase'. Never uses fallback data.
 */
export const getOffersByProductId = async (productId) => {
  if (!productId) {
    return { success: true, data: [], source: 'supabase' };
  }
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY)');
  }
  try {
    const { data: rows, error } = await supabase
      .from('latest_prices_view')
      .select('product_id, product_name, website, current_price, affiliate_url, last_checked_at')
      .eq('product_id', productId)
      .order('current_price', { ascending: true });
    if (error) {
      debugPrices('getOffersByProductId error', error.message);
      throw new Error(error.message || 'Failed to fetch offers by product_id');
    }
    const processedData = (Array.isArray(rows) ? rows : []).map(mapLatestPricesRowToUI);
    debugPrices('getOffersByProductId', productId, 'rows:', processedData.length);
    return {
      success: true,
      data: processedData,
      source: 'supabase'
    };
  } catch (err) {
    debugPrices('getOffersByProductId error', err?.message);
    throw err;
  }
};

/**
 * Fetch the best (lowest) price for a product from best_price_per_product view, if available.
 * Optional: use for badges or "From $X" without loading all offers.
 *
 * @param {string} productId - UUID (or id) of the product from the products table
 * @returns {Promise<{ success: boolean, lowestPrice: number | null, retailer: string | null, affiliate_url: string | null } | null>}
 *   Best price summary or null if view missing / no row / not configured.
 */
export const getBestPriceByProductId = async (productId) => {
  if (!productId) return null;
  if (!isSupabaseConfigured()) {
    debugPrices('getBestPriceByProductId: Supabase not configured');
    return null;
  }
  try {
    const { data, error } = await supabase
      .from('best_price_per_product')
      .select('product_id, current_price, website, affiliate_url')
      .eq('product_id', productId)
      .maybeSingle();
    if (error) {
      debugPrices('getBestPriceByProductId error (view may not exist)', error.message);
      return null;
    }
    if (!data) return null;
    const price = data.current_price != null ? Number(data.current_price) : null;
    return {
      success: true,
      lowestPrice: price,
      retailer: data.website || null,
      affiliate_url: data.affiliate_url || null
    };
  } catch (err) {
    debugPrices('getBestPriceByProductId error', err?.message);
    return null;
  }
};

/**
 * Fetch latest prices from Supabase latest_prices_view.
 * Maps view columns to the same shape the UI expects (Product, Price, Source, Job Link, etc.).
 * No-match: returns { success: true, data: [] } (graceful). LiveScope still gets fallback data.
 */
export const fetchProductPricesFromCSV = async (productId = null, csvLookupId = null, exactMatchOnly = false) => {
  const lookupId = csvLookupId || productId;
  debugPrices('fetchProductPricesSupabase', { productId, csvLookupId, lookupId, exactMatchOnly });

  try {
    if (!lookupId) {
      throw new Error('No product ID or csvLookupId provided');
    }

    const cacheKey = `${lookupId}_${exactMatchOnly}`;
    const cachedData = priceCache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
      debugPrices('cache hit', lookupId, 'items:', cachedData.data?.data?.length ?? 0);
      return cachedData.data;
    }

    const timestamp = Date.now();

    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY)');
    }

    const productNameForQuery = resolveProductNameForSupabase(lookupId);
    debugPrices('Supabase query product_name:', productNameForQuery);

    // View may expose price as "price" or "current_price" (from product_offers)
    const { data: rows, error } = await supabase
      .from('latest_prices_view')
      .select('product_name, website, current_price, affiliate_url, last_checked_at')
      .eq('product_name', productNameForQuery);

    if (error) {
      debugPrices('Supabase error', error.message, error);
      throw new Error(error.message || 'Failed to fetch prices from Supabase');
    }

    const rowCount = Array.isArray(rows) ? rows.length : 0;
    debugPrices('Supabase row count for', productNameForQuery, '=', rowCount);

    let filteredData = Array.isArray(rows) ? rows : [];
    let usingFallback = false;

    if (filteredData.length === 0) {
      if (lookupId.toLowerCase().includes('livescope')) {
        debugPrices('no match: using LiveScope fallback for', lookupId);
        usingFallback = true;
        filteredData = [
          {
            product_name: 'LiveScope Plus LVS34',
            website: 'Amazon',
            price: 1659.9,
            affiliate_url: 'https://at.fish/AmzLVS34',
            last_checked_at: new Date().toISOString()
          },
          {
            product_name: 'LiveScope Plus LVS34',
            website: 'Scheels',
            price: 1699.99,
            affiliate_url: 'https://at.fish/ScheelsLVS34',
            last_checked_at: new Date().toISOString()
          }
        ];
      } else {
        debugPrices('no match (graceful): returning success: true, data: [] for', lookupId);
        const emptyResult = {
          success: true,
          data: [],
          source: 'supabase',
          usingFallback: false,
          timestamp
        };
        priceCache.set(cacheKey, { data: emptyResult, timestamp: Date.now() });
        return emptyResult;
      }
    }

    const processedData = filteredData.map((row) => {
      const priceValue = row.current_price != null ? row.current_price : row.price;
      const { priceStr, numericPrice } = normalizePrice(priceValue);
      return {
        Price: priceStr,
        Product: row.product_name || 'Unknown Product',
        'Date Time Stamp': row.last_checked_at || new Date().toISOString(),
        Source: row.website || 'Unknown',
        'Job Link': row.affiliate_url || row.affiliate_link || '',
        numeric_price: numericPrice
      };
    });

    debugPrices('returning', processedData.length, 'items for', lookupId);

    const result = {
      success: true,
      data: processedData,
      source: 'supabase',
      usingFallback,
      timestamp
    };

    priceCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (error) {
    debugPrices('fetchProductPricesSupabase error', lookupId, error?.message);
    throw error;
  }
};

// Retry wrapper function
export const fetchProductPrices = async (productId = null, retryCount = 0, maxRetries = 4, csvLookupId = null, exactMatchOnly = false) => {
  const lookup = csvLookupId || productId;
  try {
    debugPrices('fetchProductPrices', lookup, `attempt ${retryCount + 1}/${maxRetries + 1}`);
    const result = await fetchProductPricesFromCSV(productId, csvLookupId, exactMatchOnly);
    debugPrices('fetchProductPrices success', lookup, 'items:', result?.data?.length ?? 0);
    return result;
  } catch (error) {
    if (error.message?.includes('No pricing data found from server')) {
      debugPrices('fetchProductPrices no data (expected for some products)', lookup);
      throw error;
    }
    debugPrices('fetchProductPrices error', lookup, error?.message);
    if (retryCount < maxRetries) {
      const retryDelay = Math.min(2000 + (retryCount * 1000), 8000);
      debugPrices('fetchProductPrices retry in', retryDelay, 'ms');
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return fetchProductPrices(productId, retryCount + 1, maxRetries, csvLookupId, exactMatchOnly);
    }
    throw error;
  }
};

// Simple lowest price fetcher with Amped Outdoors discount support
export const fetchLowestPrice = async (csvLookupId, exactMatchOnly = false, msrp = null, isAmpedOutdoors = false) => {
  try {
    debugPrices('fetchLowestPrice', csvLookupId, { isAmpedOutdoors });
    const result = await fetchProductPrices(null, 0, 1, csvLookupId, exactMatchOnly);

    if (result.success && result.data && result.data.length > 0) {
      const sortedData = result.data.sort((a, b) => {
        let priceA = parseFloat((a.Price || '0').replace(/[$,]/g, ''));
        let priceB = parseFloat((b.Price || '0').replace(/[$,]/g, ''));
        if (isAmpedOutdoors) {
          if (a.Source && a.Source.toLowerCase().includes('amped outdoors')) priceA = priceA * 0.9;
          if (b.Source && b.Source.toLowerCase().includes('amped outdoors')) priceB = priceB * 0.9;
        }
        return priceA - priceB;
      });

      if (sortedData.length > 0) {
        const lowestPriceItem = sortedData[0];
        let lowestPrice = parseFloat((lowestPriceItem.Price || '0').replace(/[$,]/g, ''));
        if (isAmpedOutdoors && lowestPriceItem.Source?.toLowerCase().includes('amped outdoors')) {
          lowestPrice = lowestPrice * 0.9;
          debugPrices('fetchLowestPrice Amped 10% applied', lowestPrice);
        }
        let msrpSavings = null;
        if (msrp && typeof msrp === 'number' && msrp > lowestPrice) {
          msrpSavings = { amount: (msrp - lowestPrice).toFixed(2), reference: 'MSRP' };
        }
        return {
          success: true,
          price: parseFloat(lowestPrice.toFixed(2)),
          lowestPrice: parseFloat(lowestPrice.toFixed(2)),
          data: result.data,
          source: result.source,
          msrpSavings
        };
      }
    }

    debugPrices('fetchLowestPrice no data', csvLookupId);
    return { success: false, lowestPrice: null, data: [], source: 'no-data' };
  } catch (error) {
    debugPrices('fetchLowestPrice error', csvLookupId, error?.message);
    return {
      success: false,
      lowestPrice: null,
      data: [],
      source: 'error',
      error: error.message
    };
  }
}; 